import React, { useState, useEffect } from 'react';
import { X, Calendar, User, DollarSign, Clock, Tag, FileText, Plus, Trash2, Copy, ExternalLink } from 'lucide-react';
import { api, useAuthStore } from '../auth/authStore';
import toast from 'react-hot-toast';
import { TaskScheduleForm, ScheduleFrequency, BiWeeklyPattern, MonthlyPattern, QuarterlyPattern, AnnualPattern } from './components/TaskScheduleForm';
import { extractTenantSubdomain } from '@/utils/tenantHostname';

interface TaskModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  task?: Task | null;
  mode: 'create' | 'edit';
}

interface Task {
  id: string;
  type: string;
  title: string;
  description?: string;
  clientId: string;
  clientName?: string;
  status: string;
  documentsRepositoryUrl?: string;
  assignees: {
    preparerId?: string;
    reviewerId?: string;
    submitterId?: string;
  };
  schedule: {
    frequency: string;
    dueDate: string;
    periodStart?: string;
    periodEnd?: string;
    recurrenceRule?: any;
    hasEndCondition?: boolean;
  };
  checklist: Array<{
    _id: string;
    label: string;
    done: boolean;
    required: boolean;
    order: number;
  }>;
  billing: {
    feeType: string;
    fixedAmount?: number;
    billableRate?: number;
    estimatedHours?: number;
  };
  priority: 'low' | 'medium' | 'high' | 'urgent';
  tags: string[];
}

interface WorkflowTemplate {
  id: string;
  country: string;
  name: string;
  description?: string;
  type: string;
  priority?: string;
  estimatedHours?: number;
  billing?: {
    feeType?: string;
    fixedAmount?: number;
    billableRate?: number;
  };
  tags?: string[];
  checklist?: Array<{
    label: string;
    description?: string;
    required?: boolean;
    order?: number;
  }>;
}

interface Client {
  id: string;
  displayName: string;
  legalName: string;
  address?: {
    country?: string;
  };
}

interface User {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
}

interface FormData {
  type: string;
  title: string;
  description: string;
  clientId: string;
  documentsRepositoryUrl: string;
  assignees: {
    preparerId: string;
    reviewerId: string;
    submitterId: string;
  };
  schedule: {
    frequency: string;
    dueDate: string;
    periodStart: string;
    periodEnd: string;
    recurrenceRule?: any;
    hasEndCondition?: boolean;
  };
  billing: {
    feeType: string;
    fixedAmount: string;
    billableRate: string;
    estimatedHours: string;
  };
  priority: 'low' | 'medium' | 'high' | 'urgent';
  tags: string[];
  checklist: Array<{
    label: string;
    required: boolean;
  }>;
}

const toPriorityValue = (priority?: string): 'low' | 'medium' | 'high' | 'urgent' | undefined => {
  if (!priority) return undefined;
  switch (priority.toUpperCase()) {
    case 'LOW':
      return 'low';
    case 'MEDIUM':
      return 'medium';
    case 'HIGH':
      return 'high';
    case 'URGENT':
      return 'urgent';
    default:
      return undefined;
  }
};

const toNumberString = (value?: number | null) => {
  if (value === null || value === undefined) return '';
  return `${value}`;
};

// Validation function for recurrence rules
const validateRecurrenceRule = (rule: any): string[] => {
  const errors: string[] = [];

  if (!rule.frequency) {
    errors.push('Frequency is required');
    return errors;
  }

  switch (rule.frequency) {
    case 'WEEKLY':
      if (rule.weeklyDay === null || rule.weeklyDay === undefined) {
        errors.push('Day of week is required for weekly frequency');
      }
      break;

    case 'BI_WEEKLY':
      if (!rule.biWeeklyPattern) {
        errors.push('Bi-weekly pattern is required');
      }
      if (rule.biWeeklyDay === null || rule.biWeeklyDay === undefined) {
        errors.push('Day of week is required for bi-weekly frequency');
      }
      break;

    case 'MONTHLY':
      if (!rule.monthlyPattern) {
        errors.push('Monthly pattern is required');
      } else {
        if (rule.monthlyPattern === 'SPECIFIC_DAY' && (!rule.monthlyDay || rule.monthlyDay < 1 || rule.monthlyDay > 31)) {
          errors.push('Valid day of month (1-31) is required for specific day pattern');
        }
        if (rule.monthlyPattern === 'NTH_WEEKDAY') {
          if (rule.monthlyNthOccurrence === null || rule.monthlyNthOccurrence === undefined) {
            errors.push('Nth occurrence is required for nth weekday pattern');
          }
          if (rule.monthlyWeekday === null || rule.monthlyWeekday === undefined) {
            errors.push('Weekday is required for nth weekday pattern');
          }
        }
      }
      break;

    case 'QUARTERLY':
      if (!rule.quarterlyPattern) {
        errors.push('Quarterly pattern is required');
      } else if ((rule.quarterlyPattern === 'NTH_DAY_OF_FIRST_MONTH' || rule.quarterlyPattern === 'NTH_DAY_OF_LAST_MONTH') &&
                 (!rule.quarterlyDay || rule.quarterlyDay < 1 || rule.quarterlyDay > 31)) {
        errors.push('Valid day (1-31) is required for quarterly day patterns');
      }
      break;

    case 'ANNUALLY':
      if (!rule.annualPattern) {
        errors.push('Annual pattern is required');
      } else {
        if (rule.annualPattern === 'SPECIFIC_DATE') {
          if (!rule.annualMonth || rule.annualMonth < 1 || rule.annualMonth > 12) {
            errors.push('Valid month (1-12) is required for specific date pattern');
          }
          if (!rule.annualDay || rule.annualDay < 1 || rule.annualDay > 31) {
            errors.push('Valid day (1-31) is required for specific date pattern');
          }
        } else if (rule.annualPattern === 'NTH_WEEKDAY_OF_MONTH') {
          if (!rule.annualMonth || rule.annualMonth < 1 || rule.annualMonth > 12) {
            errors.push('Valid month (1-12) is required for nth weekday pattern');
          }
          if (rule.annualNthOccurrence === null || rule.annualNthOccurrence === undefined) {
            errors.push('Nth occurrence is required for nth weekday pattern');
          }
          if (rule.annualWeekday === null || rule.annualWeekday === undefined) {
            errors.push('Weekday is required for nth weekday pattern');
          }
        }
      }
      break;
  }

  return errors;
};

// Documents Repository Field Component
interface DocumentsRepositoryFieldProps {
  value: string;
  onChange: (value: string) => void;
  mode: 'create' | 'edit';
}

const DocumentsRepositoryField: React.FC<DocumentsRepositoryFieldProps> = ({ value, onChange, mode }) => {
  const { user } = useAuthStore();

  // Check if user has admin privileges (can edit the URL)
  const isAdmin = user?.roles?.some(role =>
    ['SuperAdmin', 'SUPER_ADMIN', 'FirmAdmin', 'FIRM_ADMIN'].includes(role)
  ) || false;

  const copyToClipboard = async () => {
    if (value) {
      try {
        await navigator.clipboard.writeText(value);
        toast.success('URL copied to clipboard');
      } catch (err) {
        toast.error('Failed to copy URL');
      }
    }
  };

  const openUrl = () => {
    if (value) {
      window.open(value, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        Documents Repository URL
        {!isAdmin && (
          <span className="text-xs text-gray-500 ml-2">(Admin only)</span>
        )}
      </label>

      {isAdmin ? (
        // Admin can edit the URL
        <div className="relative">
          <input
            type="url"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className="w-full px-3 py-2 pr-20 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            placeholder="https://example.com/documents"
          />
          {value && (
            <div className="absolute right-2 top-1/2 transform -translate-y-1/2 flex gap-1">
              <button
                type="button"
                onClick={copyToClipboard}
                className="p-1 text-gray-400 hover:text-gray-600"
                title="Copy URL"
              >
                <Copy className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={openUrl}
                className="p-1 text-gray-400 hover:text-gray-600"
                title="Open URL"
              >
                <ExternalLink className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      ) : (
        // Non-admin users see read-only field with copy functionality
        <div className="relative">
          {value ? (
            <div className="flex items-center gap-2 p-3 bg-gray-50 border border-gray-200 rounded-lg">
              <span className="flex-1 text-sm text-gray-700 break-all">{value}</span>
              <div className="flex gap-1">
                <button
                  type="button"
                  onClick={copyToClipboard}
                  className="p-1 text-gray-400 hover:text-gray-600"
                  title="Copy URL"
                >
                  <Copy className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={openUrl}
                  className="p-1 text-gray-400 hover:text-gray-600"
                  title="Open URL"
                >
                  <ExternalLink className="w-4 h-4" />
                </button>
              </div>
            </div>
          ) : (
            <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-500">
              No document repository URL set
            </div>
          )}
        </div>
      )}

      {value && (
        <p className="text-xs text-gray-500 mt-1">
          Click the copy button to copy the URL or the external link button to open it
        </p>
      )}
    </div>
  );
};

export const TaskModal: React.FC<TaskModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  task,
  mode
}) => {
  const { tenant } = useAuthStore();
  const [loading, setLoading] = useState(false);
  const [clients, setClients] = useState<Client[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [dataLoading, setDataLoading] = useState(false);
  const [workflowTemplates, setWorkflowTemplates] = useState<WorkflowTemplate[]>([]);
  const [workflowLoading, setWorkflowLoading] = useState(false);
  const [selectedWorkflowId, setSelectedWorkflowId] = useState('');
  const [allWorkflowTemplates, setAllWorkflowTemplates] = useState<WorkflowTemplate[]>([]);
  const [formData, setFormData] = useState<FormData>({
    type: 'TAX_RETURN',
    title: '',
    description: '',
    clientId: '',
    documentsRepositoryUrl: '',
    assignees: {
      preparerId: '',
      reviewerId: '',
      submitterId: '',
    },
    schedule: {
      frequency: 'ONE_TIME',
      dueDate: '',
      periodStart: '',
      periodEnd: '',
      recurrenceRule: { frequency: 'ONE_TIME' },
      hasEndCondition: false,
    },
    billing: {
      feeType: 'HOURLY',
      fixedAmount: '',
      billableRate: '',
      estimatedHours: '',
    },
    priority: 'medium',
    tags: [],
    checklist: [],
  });
  const [newTag, setNewTag] = useState('');
  const [newChecklistItem, setNewChecklistItem] = useState('');

  useEffect(() => {
    if (isOpen) {
      setDataLoading(true);
      Promise.all([fetchClients(), fetchUsers(), fetchWorkflowTemplates()]).finally(() => {
        setDataLoading(false);
      });

      // Reset form data when switching modes or opening modal
      if (mode === 'create') {
        setSelectedWorkflowId('');
        setFormData({
          type: 'TAX_RETURN',
          title: '',
          description: '',
          clientId: '',
          documentsRepositoryUrl: '',
          assignees: {
            preparerId: '',
            reviewerId: '',
            submitterId: '',
          },
          schedule: {
            frequency: 'ONE_TIME',
            dueDate: '',
            periodStart: '',
            periodEnd: '',
            recurrenceRule: { frequency: 'ONE_TIME' },
            hasEndCondition: false,
          },
          billing: {
            feeType: 'HOURLY',
            fixedAmount: '',
            billableRate: '',
            estimatedHours: '',
          },
          priority: 'medium',
          tags: [],
          checklist: [],
        });
      }
    }
  }, [isOpen, mode]);

  const fetchWorkflowTemplates = async (): Promise<void> => {
    try {
      setWorkflowLoading(true);
      const headers: Record<string, string> = {};
      const hostSub = extractTenantSubdomain(window.location.hostname);
      if (tenant?.subdomain) headers['X-Firm'] = tenant.subdomain;
      if (hostSub) headers['X-Firm'] = hostSub;
      if (tenant?.id) headers['X-Tenant'] = tenant.id;

      const response = await api.get('/firm-admin/workflows/country-templates', { headers });
      if (response.data?.success) {
        const templates = response.data.data || [];
        setAllWorkflowTemplates(templates);
        setWorkflowTemplates(templates);
      } else {
        setAllWorkflowTemplates([]);
        setWorkflowTemplates([]);
      }
    } catch (error: any) {
      console.error('Error fetching workflow templates:', error);
      const errorMessage =
        error.response?.data?.message || error.response?.data?.error?.message || 'Failed to load workflow templates';
      toast.error(errorMessage);
    } finally {
      setWorkflowLoading(false);
    }
  };

  const applyWorkflowTemplate = (template?: WorkflowTemplate | null) => {
    if (!template) return;

    setFormData(prev => ({
      ...prev,
      type: template.type || prev.type,
      priority: toPriorityValue(template.priority) || prev.priority,
      title: prev.title?.trim() ? prev.title : template.name,
      description: prev.description?.trim() ? prev.description : (template.description || ''),
      billing: {
        ...prev.billing,
        feeType: template.billing?.feeType || prev.billing.feeType,
        fixedAmount: template.billing?.fixedAmount !== undefined ? toNumberString(template.billing.fixedAmount) : prev.billing.fixedAmount,
        billableRate: template.billing?.billableRate !== undefined ? toNumberString(template.billing.billableRate) : prev.billing.billableRate,
        estimatedHours: template.estimatedHours !== undefined ? toNumberString(template.estimatedHours) : prev.billing.estimatedHours,
      },
      tags: template.tags && template.tags.length > 0 ? template.tags : prev.tags,
      checklist: template.checklist && template.checklist.length > 0
        ? template.checklist.map(item => ({
            label: item.label,
            required: item.required ?? true,
          }))
        : prev.checklist,
    }));
  };

  // Separate effect to set form data after clients and users are loaded
  useEffect(() => {
    if (isOpen && mode === 'edit' && task && clients.length > 0 && users.length > 0) {
      console.log('=== TASK MODAL DEBUG ===');
      console.log('Task object:', JSON.stringify(task, null, 2));
      console.log('Task clientId:', task.clientId);
      console.log('Available clients:', clients.map(c => ({ id: c.id, name: c.displayName || c.legalName })));

      // Only use ID-based matching - no fallbacks
      const matchedClient = clients.find(c => c.id === task.clientId);
      console.log('Client match found:', matchedClient);

      if (!matchedClient) {
        console.error(`❌ Data integrity issue: Task references client ID "${task.clientId}" which does not exist in current client list. Please fix the database.`);
      }

      const formDataToSet = {
        type: task.type,
        title: task.title,
        description: task.description || '',
        clientId: task.clientId || '',
        documentsRepositoryUrl: task.documentsRepositoryUrl || '',
        assignees: {
          preparerId: task.assignees?.preparerId || '',
          reviewerId: task.assignees?.reviewerId || '',
          submitterId: task.assignees?.submitterId || '',
        },
        schedule: {
          frequency: task.schedule?.frequency || 'ONE_TIME',
          dueDate: task.schedule?.dueDate ? task.schedule.dueDate.split('T')[0] : '',
          periodStart: task.schedule?.periodStart ? task.schedule.periodStart.split('T')[0] : '',
          periodEnd: task.schedule?.periodEnd ? task.schedule.periodEnd.split('T')[0] : '',
          recurrenceRule: task.schedule?.recurrenceRule || { frequency: task.schedule?.frequency || 'ONE_TIME' },
          hasEndCondition: !!(task.schedule?.periodEnd || task.schedule?.recurrenceRule?.maxOccurrences),
        },
        billing: {
          feeType: task.billing?.feeType || 'HOURLY',
          fixedAmount: task.billing?.fixedAmount?.toString() || '',
          billableRate: task.billing?.billableRate?.toString() || '',
          estimatedHours: task.billing?.estimatedHours?.toString() || '',
        },
        priority: task.priority || 'medium',
        tags: task.tags ? [...task.tags] : [],
        checklist: task.checklist ? task.checklist.map(item => ({
          label: item.label,
          required: item.required,
        })) : [],
      };

      console.log('Form data being set:', JSON.stringify(formDataToSet, null, 2));
      setFormData(formDataToSet);
      console.log('=== END TASK MODAL DEBUG ===');
    }
  }, [isOpen, mode, task, clients.length, users.length]);

  const fetchClients = async (): Promise<void> => {
    try {
      const response = await api.get('/clients');
      if (response.data.success) {
        setClients(response.data.data.clients || []);
      }
    } catch (error) {
      console.error('Error fetching clients:', error);
    }
  };

  const fetchUsers = async (): Promise<void> => {
    try {
      const response = await api.get('/firm-admin/users');
      if (response.data.success) {
        setUsers(response.data.data.users || []);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.title.trim()) {
      toast.error('Task title is required');
      return;
    }
    
    if (!formData.clientId) {
      toast.error('Please select a client');
      return;
    }
    
    // Due date is only required for one-time tasks
    if (formData.schedule.frequency === 'ONE_TIME' && !formData.schedule.dueDate) {
      toast.error('Due date is required for one-time tasks');
      return;
    }

    // Validate recurrence rule
    if (formData.schedule.frequency !== 'ONE_TIME' && formData.schedule.recurrenceRule) {
      const validationErrors = validateRecurrenceRule(formData.schedule.recurrenceRule);
      if (validationErrors.length > 0) {
        toast.error(`Scheduling error: ${validationErrors[0]}`);
        return;
      }
    }

    setLoading(true);
    
    try {
      const payload = {
        type: formData.type,
        title: formData.title.trim(),
        description: formData.description.trim() || undefined,
        clientId: formData.clientId,
        workflowId: selectedWorkflowId || undefined,
        documentsRepositoryUrl: formData.documentsRepositoryUrl.trim() || undefined,
        assignees: {
          preparerId: formData.assignees.preparerId || '',
          reviewerId: formData.assignees.reviewerId || '',
          submitterId: formData.assignees.submitterId || '',
        },
        schedule: {
          frequency: formData.schedule.frequency,
          ...(formData.schedule.dueDate && {
            dueDate: new Date(formData.schedule.dueDate).toISOString()
          }),
          ...(formData.schedule.periodStart && {
            periodStart: new Date(formData.schedule.periodStart).toISOString()
          }),
          ...(formData.schedule.periodEnd && {
            periodEnd: new Date(formData.schedule.periodEnd).toISOString()
          }),
          ...(formData.schedule.recurrenceRule && {
            recurrenceRule: formData.schedule.recurrenceRule
          }),
          // Always include hasEndCondition to properly handle clearing periodEnd
          hasEndCondition: formData.schedule.hasEndCondition || false,
        },
        billing: {
          feeType: formData.billing.feeType,
          ...(formData.billing.fixedAmount && { fixedAmount: parseFloat(formData.billing.fixedAmount) }),
          ...(formData.billing.billableRate && { billableRate: parseFloat(formData.billing.billableRate) }),
          ...(formData.billing.estimatedHours && { estimatedHours: parseFloat(formData.billing.estimatedHours) }),
        },
        priority: formData.priority,
        tags: formData.tags,
        checklist: formData.checklist.map((item, index) => ({
          label: item.label,
          required: item.required,
          order: index,
        })),
      };

      console.log('Payload being sent to server:', JSON.stringify(payload, null, 2));

      const response = mode === 'create'
        ? await api.post('/firm-admin/tasks', payload)
        : await api.patch(`/firm-admin/tasks/${task?.id}`, payload);

      if (response.data.success) {
        toast.success(`Task ${mode === 'create' ? 'created' : 'updated'} successfully`);
        onSuccess();
        onClose();
      }
    } catch (error: any) {
      console.error(`Error ${mode === 'create' ? 'creating' : 'updating'} task:`, error);
      toast.error(error.response?.data?.message || `Failed to ${mode} task`);
    } finally {
      setLoading(false);
    }
  };

  const addTag = () => {
    if (newTag.trim() && !formData.tags.includes(newTag.trim().toLowerCase())) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, newTag.trim().toLowerCase()]
      }));
      setNewTag('');
    }
  };

  const removeTag = (tagToRemove: string) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  const addChecklistItem = () => {
    if (newChecklistItem.trim()) {
      setFormData(prev => ({
        ...prev,
        checklist: [...prev.checklist, { label: newChecklistItem.trim(), required: true }]
      }));
      setNewChecklistItem('');
    }
  };

  const removeChecklistItem = (index: number) => {
    setFormData(prev => ({
      ...prev,
      checklist: prev.checklist.filter((_, i) => i !== index)
    }));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">
            {mode === 'create' ? 'Create New Task' : 'Edit Task'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {mode === 'create' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Workflow Template (Optional)
              </label>
              <select
                value={selectedWorkflowId}
                onChange={(e) => {
                  const nextId = e.target.value;
                  setSelectedWorkflowId(nextId);
                  const selectedTemplate = workflowTemplates.find(template => template.id === nextId);
                  applyWorkflowTemplate(selectedTemplate);
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                disabled={workflowLoading}
              >
                <option value="">{workflowLoading ? 'Loading workflows...' : 'No workflow selected'}</option>
                {workflowTemplates.map((template) => (
                  <option key={template.id} value={template.id}>
                    {template.country?.toUpperCase()} — {template.name}
                  </option>
                ))}
              </select>
              {selectedWorkflowId && (
                <p className="text-xs text-gray-500 mt-1">
                  Selecting a workflow will prefill type, priority, billing, tags, and checklist.
                </p>
              )}
            </div>
          )}
          {/* Basic Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Task Type *
              </label>
              <select
                value={formData.type}
                onChange={(e) => setFormData(prev => ({ ...prev, type: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                required
              >
                <option value="TAX_RETURN">Tax Return</option>
                <option value="BOOKKEEPING">Bookkeeping</option>
                <option value="PAYROLL">Payroll</option>
                <option value="AUDIT">Audit</option>
                <option value="CONSULTATION">Consultation</option>
                <option value="OTHER">Other</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Priority
              </label>
              <select
                value={formData.priority}
                onChange={(e) => setFormData(prev => ({ ...prev, priority: e.target.value as any }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Task Title *
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="Enter task title"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="Enter task description"
            />
          </div>

          {/* Documents Repository URL Field */}
          <DocumentsRepositoryField
            value={formData.documentsRepositoryUrl}
            onChange={(value) => setFormData(prev => ({ ...prev, documentsRepositoryUrl: value }))}
            mode={mode}
          />

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Client *
            </label>
            <select
              value={formData.clientId}
              onChange={(e) => {
                const newClientId = e.target.value;
                setFormData(prev => ({ ...prev, clientId: newClientId }));
                setSelectedWorkflowId('');
                // Filter workflow templates by the selected client's country
                if (newClientId) {
                  const selectedClient = clients.find(c => c.id === newClientId);
                  const clientCountry = selectedClient?.address?.country;
                  if (clientCountry) {
                    const filtered = allWorkflowTemplates.filter(
                      t => t.country.toLowerCase() === clientCountry.toLowerCase()
                    );
                    setWorkflowTemplates(filtered);
                  } else {
                    setWorkflowTemplates(allWorkflowTemplates);
                  }
                } else {
                  setWorkflowTemplates(allWorkflowTemplates);
                }
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              required
              disabled={dataLoading}
            >
              <option value="">{dataLoading ? 'Loading clients...' : 'Select a client'}</option>
              {clients.map(client => (
                <option key={client.id} value={client.id}>
                  {client.displayName || client.legalName}
                </option>
              ))}
            </select>
            {/* Debug info and data integrity warnings */}
            {mode === 'edit' && (
              <div className="text-xs mt-1">
                <div className="text-gray-500">
                  Debug: formData.clientId = "{formData.clientId}", clients loaded = {clients.length}
                </div>
                {task && task.clientId && !clients.find(c => c.id === task.clientId) && (
                  <div className="text-red-600 bg-red-50 p-2 rounded mt-1">
                    ❌ Data integrity issue: Task references client ID "{task.clientId}" which does not exist. Please fix the database and select the correct client manually.
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Assignees */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Assignees</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Preparer
                </label>
                <select
                  value={formData.assignees.preparerId}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    assignees: { ...prev.assignees, preparerId: e.target.value }
                  }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  disabled={dataLoading}
                >
                  <option value="">{dataLoading ? 'Loading users...' : 'Select preparer'}</option>
                  {users.map(user => (
                    <option key={user.id} value={user.id}>
                      {user.firstName} {user.lastName}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Reviewer
                </label>
                <select
                  value={formData.assignees.reviewerId}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    assignees: { ...prev.assignees, reviewerId: e.target.value }
                  }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  disabled={dataLoading}
                >
                  <option value="">{dataLoading ? 'Loading users...' : 'Select reviewer'}</option>
                  {users.map(user => (
                    <option key={user.id} value={user.id}>
                      {user.firstName} {user.lastName}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Submitter
                </label>
                <select
                  value={formData.assignees.submitterId}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    assignees: { ...prev.assignees, submitterId: e.target.value }
                  }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  disabled={dataLoading}
                >
                  <option value="">{dataLoading ? 'Loading users...' : 'Select submitter'}</option>
                  {users.map(user => (
                    <option key={user.id} value={user.id}>
                      {user.firstName} {user.lastName}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Schedule */}
          <TaskScheduleForm
            schedule={formData.schedule}
            onChange={(schedule) => setFormData(prev => ({ ...prev, schedule }))}
            errors={{}}
          />

          {/* Billing */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Billing</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Fee Type
                </label>
                <select
                  value={formData.billing.feeType}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    billing: { ...prev.billing, feeType: e.target.value }
                  }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="HOURLY">Hourly</option>
                  <option value="FIXED">Fixed</option>
                  <option value="NO_CHARGE">No Charge</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Estimated Hours
                </label>
                <input
                  type="number"
                  step="0.5"
                  min="0"
                  value={formData.billing.estimatedHours}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    billing: { ...prev.billing, estimatedHours: e.target.value }
                  }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="0.0"
                />
              </div>

              {formData.billing.feeType === 'FIXED' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Fixed Amount ($)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.billing.fixedAmount}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      billing: { ...prev.billing, fixedAmount: e.target.value }
                    }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="0.00"
                  />
                </div>
              )}

              {formData.billing.feeType === 'HOURLY' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Billable Rate ($/hour)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.billing.billableRate}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      billing: { ...prev.billing, billableRate: e.target.value }
                    }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="0.00"
                  />
                </div>
              )}
            </div>
          </div>

          {/* Tags */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Tags
            </label>
            <div className="flex flex-wrap gap-2 mb-2">
              {formData.tags.map((tag, index) => (
                <span
                  key={index}
                  className="inline-flex items-center px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full"
                >
                  {tag}
                  <button
                    type="button"
                    onClick={() => removeTag(tag)}
                    className="ml-1 text-blue-600 hover:text-blue-800"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <input
                type="text"
                value={newTag}
                onChange={(e) => setNewTag(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="Add a tag"
              />
              <button
                type="button"
                onClick={addTag}
                className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
              >
                <Plus className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Checklist */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Checklist
            </label>
            <div className="space-y-2 mb-4">
              {formData.checklist.map((item, index) => (
                <div key={index} className="flex items-center gap-2 p-2 bg-gray-50 rounded">
                  <input
                    type="checkbox"
                    checked={item.required}
                    onChange={(e) => {
                      const newChecklist = [...formData.checklist];
                      newChecklist[index].required = e.target.checked;
                      setFormData(prev => ({ ...prev, checklist: newChecklist }));
                    }}
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="flex-1 text-sm">{item.label}</span>
                  <button
                    type="button"
                    onClick={() => removeChecklistItem(index)}
                    className="text-red-600 hover:text-red-800"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <input
                type="text"
                value={newChecklistItem}
                onChange={(e) => setNewChecklistItem(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addChecklistItem())}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="Add checklist item"
              />
              <button
                type="button"
                onClick={addChecklistItem}
                className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
              >
                <Plus className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex justify-end gap-4 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={loading}
            >
              {loading ? 'Saving...' : (mode === 'create' ? 'Create Task' : 'Update Task')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
